import { useState } from 'react';
import type { FormEvent } from 'react';

const FORM_ID = 'kanliao-subscribe-form';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/daikq03p14a1h3mr75tg';

export default function SubscribeForm({ compact = false }: { compact?: boolean }) {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const text = String(value).trim();
      if (!text) return;
      payload.append(key, text);
    });

    setStatus('submitting');
    setFormError('');
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = String(serverMsg).toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        setFormError('');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg ? `提交未成功：${serverMsg}` : '提交未成功，请稍后再试。');
      }
    } catch (err) {
      setStatus('error');
      setFormError('网络异常，提交未成功，请稍后再试。');
    }
  };

  return (
    <form
      id={FORM_ID}
      data-readdy-form
      onSubmit={handleSubmit}
      className={`space-y-4 ${compact ? '' : 'rounded-xl border border-background-200 bg-background-100 p-5 md:p-7'}`}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${FORM_ID}-email`} className="block text-sm font-medium text-foreground-800 mb-1.5">
            邮箱地址
          </label>
          <input
            id={`${FORM_ID}-email`}
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="you@example.com"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div>
          <label htmlFor={`${FORM_ID}-type`} className="block text-sm font-medium text-foreground-800 mb-1.5">
            提交类型
          </label>
          <select
            id={`${FORM_ID}-type`}
            name="type"
            defaultValue="订阅热料周报"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          >
            <option value="订阅热料周报">订阅热料周报</option>
            <option value="提供新线索">提供新线索</option>
            <option value="指出核实错误">指出核实错误</option>
          </select>
        </div>
      </div>

      <div>
        <label htmlFor={`${FORM_ID}-message`} className="block text-sm font-medium text-foreground-800 mb-1.5">
          补充说明（选填，最多 500 字）
        </label>
        <textarea
          id={`${FORM_ID}-message`}
          name="message"
          rows={compact ? 2 : 3}
          maxLength={500}
          placeholder="如果你要提供线索或指出错误，可以在这里简单说明，例如爆料链接、时间点或你的核对依据。"
          className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 resize-none"
        ></textarea>
      </div>

      <div className="newsletter-hp-field" aria-hidden="true">
        <label htmlFor={`${FORM_ID}-website`}>Website</label>
        <input
          id={`${FORM_ID}-website`}
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
          </span>
          {status === 'submitting' ? '提交中…' : '提交'}
        </button>
        <p className="text-xs text-foreground-500">
          我们只会在核实或推送周报时联系你，不会用于其他用途。
        </p>
      </div>

      {status === 'success' && (
        <p className="flex items-start gap-2 rounded-md bg-secondary-100 px-3.5 py-2.5 text-sm text-secondary-900">
          <span className="w-4 h-4 mt-0.5 flex items-center justify-center">
            <i className="ri-checkbox-circle-line"></i>
          </span>
          已收到，感谢你的支持。如果是纠错反馈，我们核实后会尽快更新。
        </p>
      )}
      {status === 'error' && (
        <p className="flex items-start gap-2 rounded-md bg-primary-100 px-3.5 py-2.5 text-sm text-primary-800">
          <span className="w-4 h-4 mt-0.5 flex items-center justify-center">
            <i className="ri-error-warning-line"></i>
          </span>
          {formError || '提交未成功，请稍后再试。'}
        </p>
      )}
    </form>
  );
}